import { useAppSelector } from '@frontend/common';
import { changePage, listStore } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React, { useMemo } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { PaginationNavigationButton } from './pagination-navigation-button';
import { PaginationNumber } from './pagination-number';

interface PaginationButtonNavigationProps {
    id?: string;
    dispatch: ThunkDispatch<any, any, Action>;
    listStoreId: string;

    className?: string;
    allowBack?: boolean;
    backDisabled?: boolean;
    allowAllBack?: boolean;
    allBackDisabled?: boolean;
    allowForward?: boolean;
    forwardDisabled?: boolean;
    allowAllForward?: boolean;
    allForwardDisabled?: boolean;
    paginationButtonProps?: React.ComponentProps<typeof PaginationNavigationButton>;

    /**
     * When true, Prev/Next show icons only (labels move to aria-label).
     * Use in narrow containers such as half-width insight cards.
     */
    hideLabels?: boolean;

    /**
     * @description The amount of pagination numbers to display
     * Will always be odd (if an even number is given, it will be increased by 1)
     * @default 4
     */
    paginationNumberButtonAmount?: number;
    paginationNumberProps?: React.ComponentProps<typeof PaginationNumber>;
}
export const PaginationButtonNavigationClass = 'size- inline-flex justify-start items-center gap-3 m-0';

export const PaginationButtonNavigation = (props: PaginationButtonNavigationProps) => {
    const intl = useIntl();
    const store = useAppSelector(useSelector, listStore);
    const hideLabels = props.hideLabels === true;
    const pageInfo = useMemo(() => {
        const list = store[props.listStoreId];
        if (list && list.count !== null) {
            return {
                pageIndex: list.pageIndex,
                pageCount: Math.ceil(list.count / list.pageSize)
            };
        }
        return undefined;
    }, [store[props.listStoreId]]);
    const pagesToDisplay = useMemo(() => {
        if (pageInfo) return getPagesToDisplay(pageInfo.pageCount, pageInfo.pageIndex + 1, props.paginationNumberButtonAmount);
        return [];
    }, [pageInfo, props.paginationNumberButtonAmount]);
    const bwDisabled = !pageInfo || pageInfo.pageIndex <= 0;
    const fwDisabled = !pageInfo || pageInfo.pageIndex + 1 >= pageInfo.pageCount;

    const gotoPage = (index: number) => {
        if (!pageInfo) return;
        props.dispatch(changePage({ listId: props.listStoreId, page: index }));
    };

    const previousPage = () => {
        if (!pageInfo) return;
        props.dispatch(changePage({ listId: props.listStoreId, page: pageInfo!.pageIndex - 1 }));
    };

    const nextPage = () => {
        if (!pageInfo) return;
        props.dispatch(changePage({ listId: props.listStoreId, page: pageInfo!.pageIndex + 1 }));
    };

    const previousPageLabel = intl.formatMessage({
        id: 'pagination.previousPage',
        description: 'The label for the previous page button',
        defaultMessage: 'Previous page'
    });
    const nextPageLabel = intl.formatMessage({
        id: 'pagination.nextPage',
        description: 'The label for the next page button',
        defaultMessage: 'Next page'
    });

    return (
        <ul
            id={props.id}
            className={props.className ?? PaginationButtonNavigationClass}>
            {pageInfo && (
                <>
                    {props.allowBack !== false && (
                        <PaginationNavigationButton
                            {...props.paginationButtonProps}
                            id={props.id}
                            disabled={props.backDisabled ?? bwDisabled}
                            onClick={previousPage}
                            ariaLabel={hideLabels ? previousPageLabel : props.paginationButtonProps?.ariaLabel}
                            styles={props.paginationButtonProps?.styles}>
                            <LuChevronLeft
                                className={
                                    (props.backDisabled ?? bwDisabled)
                                        ? 'text-border-default justify-start text-base font-medium'
                                        : 'text-brand-muted justify-start text-base font-medium'
                                }
                            />
                            {!hideLabels && (
                                <div
                                    className={
                                        (props.backDisabled ?? bwDisabled)
                                            ? 'text-border-default justify-start text-base font-medium'
                                            : 'text-brand-muted justify-start text-base font-medium'
                                    }>
                                    <FormattedMessage
                                        id='pagination.previousPage'
                                        description='The label for the previous page button'
                                        defaultMessage='Previous page'
                                    />
                                </div>
                            )}
                        </PaginationNavigationButton>
                    )}

                    {pagesToDisplay.map((n, index) => {
                        return (
                            <PaginationNumber
                                id={props.id}
                                key={n}
                                index={n}
                                highlighted={pageInfo.pageIndex + 1 === n}
                                onClick={(i) => gotoPage(i - 1)}
                                pageDropdown={
                                    pageInfo.pageCount > 6 &&
                                    ((index === 1 && pageInfo.pageIndex > 2) || (index === 5 && pageInfo.pageIndex < pageInfo.pageCount - 3))
                                }
                                pageCount={pageInfo.pageCount}
                                {...props.paginationNumberProps}
                            />
                        );
                    })}

                    {props.allowForward !== false && (
                        <PaginationNavigationButton
                            {...props.paginationButtonProps}
                            id={props.id}
                            disabled={props.forwardDisabled ?? fwDisabled}
                            onClick={nextPage}
                            ariaLabel={hideLabels ? nextPageLabel : props.paginationButtonProps?.ariaLabel}
                            styles={props.paginationButtonProps?.styles}>
                            {!hideLabels && (
                                <div
                                    className={
                                        (props.forwardDisabled ?? fwDisabled)
                                            ? 'text-border-default justify-start text-base font-medium'
                                            : 'text-brand-muted justify-start text-base font-medium'
                                    }>
                                    <FormattedMessage
                                        id='pagination.nextPage'
                                        description='The label for the next page button'
                                        defaultMessage='Next page'
                                    />
                                </div>
                            )}
                            <LuChevronRight
                                className={
                                    (props.forwardDisabled ?? fwDisabled)
                                        ? 'text-border-default justify-start text-base font-medium'
                                        : 'text-brand-muted justify-start text-base font-medium'
                                }
                            />
                        </PaginationNavigationButton>
                    )}
                </>
            )}
        </ul>
    );
};
export default PaginationButtonNavigation;

function getPagesToDisplay(pageCount: number, currentPage: number, pagesToDisplay = 7): number[] {
    if (pageCount <= pagesToDisplay) {
        return Array.from({ length: pageCount }, (_, i) => i + 1);
    }

    const firstPage = 1;
    const lastPage = pageCount;
    let pages: number[] = [];

    pages.push(firstPage);

    const slots = pagesToDisplay - 2;
    let startPage = Math.max(2, currentPage - Math.floor(slots / 2));
    let endPage = startPage + slots - 1;

    if (endPage > lastPage - 1) {
        endPage = lastPage - 1;
        startPage = endPage - slots + 1;
        if (startPage < 2) startPage = 2;
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    pages.push(lastPage);

    return pages;
}
